import { Link } from "react-router-dom";
import { Instagram, Facebook, Mail, Phone, MapPin, Clock } from "lucide-react";
import { FOOTER } from "@/constants/testIds";
import { BRAND } from "@/data/site";

const NAV = [
  { to: "/", label: "Home" },
  { to: "/portfolio", label: "Portfolio" },
  { to: "/vrij-werk", label: "Vrij werk" },
  { to: "/huwelijk", label: "Huwelijk" },
  { to: "/zwangerschap", label: "Zwangerschap & Newborn" },
  { to: "/gezin", label: "Gezin" },
  { to: "/communie", label: "Communie" },
  { to: "/huisdieren", label: "Huisdieren" },
  { to: "/bedrijven", label: "Bedrijven" },
  { to: "/events", label: "Events" },
  { to: "/magic-mirror", label: "Magic Mirror Fotobooth" },
  { to: "/audio-gastenboek", label: "Audio Gastenboek" },
  { to: "/cadeaubonnen", label: "Cadeaubonnen" },
  { to: "/opties", label: "Opties" },
  { to: "/over-eric", label: "Over Eric" },
  { to: "/contact", label: "Contact" },
];

const Footer = () => {
  return (
    <footer className="bg-ink text-white/80 pt-24 md:pt-32 pb-10">
      <div className="container-x grid grid-cols-1 md:grid-cols-12 gap-12">
        {/* Brand */}
        <div className="md:col-span-5">
          <Link to="/" data-testid={FOOTER.logo} className="inline-flex items-center">
            <img
              src={BRAND.logo}
              alt="Lenslopers Fotografie"
              className="h-24 md:h-28 w-auto"
            />
          </Link>
          <p className="mt-6 max-w-md text-sm leading-relaxed text-white/60">
            Herinneringen die je voor altijd koestert. Ik leg vast wat er tussen jullie leeft — subtiel,
            warm en tijdloos.
          </p>
        </div>

        {/* Sitemap */}
        <div className="md:col-span-4">
          <p className="eyebrow mb-6">Sitemap</p>
          <ul className="grid grid-cols-2 gap-y-2 gap-x-6 text-sm">
            {NAV.map((n) => (
              <li key={n.to}>
                <Link to={n.to} className="hover:text-gold transition-colors">
                  {n.label}
                </Link>
              </li>
            ))}
          </ul>
        </div>

        {/* Contact */}
        <div className="md:col-span-3">
          <p className="eyebrow mb-6">Contact</p>
          <ul className="space-y-3 text-sm">
            <li className="flex items-start gap-3">
              <Phone size={14} className="mt-1 text-gold" />
              <a href={`tel:${BRAND.phone}`} data-testid={FOOTER.phone} className="hover:text-gold transition-colors">
                {BRAND.phoneDisplay}
              </a>
            </li>
            <li className="flex items-start gap-3">
              <Mail size={14} className="mt-1 text-gold" />
              <a href={`mailto:${BRAND.email}`} data-testid={FOOTER.email} className="hover:text-gold transition-colors">
                {BRAND.email}
              </a>
            </li>
            <li className="flex items-start gap-3">
              <MapPin size={14} className="mt-1 text-gold" />
              <span>{BRAND.address}</span>
            </li>
            <li className="flex items-start gap-3">
              <Clock size={14} className="mt-1 text-gold" />
              <div>
                {BRAND.openingHours.map((h) => (
                  <div key={h.day} className="text-white/70">
                    <span className="text-white/50 mr-2">{h.day}</span>
                    {h.hours}
                  </div>
                ))}
              </div>
            </li>
          </ul>

          <div className="flex items-center gap-4 mt-8">
            <a
              data-testid={FOOTER.socialInstagram}
              href={BRAND.social.instagram}
              target="_blank"
              rel="noreferrer"
              className="text-white/70 hover:text-gold transition-colors"
              aria-label="Instagram"
            >
              <Instagram size={18} />
            </a>
            <a
              data-testid={FOOTER.socialFacebook}
              href={BRAND.social.facebook}
              target="_blank"
              rel="noreferrer"
              className="text-white/70 hover:text-gold transition-colors"
              aria-label="Facebook"
            >
              <Facebook size={18} />
            </a>
            <a
              data-testid={FOOTER.socialPinterest}
              href={BRAND.social.pinterest}
              target="_blank"
              rel="noreferrer"
              className="text-white/70 hover:text-gold transition-colors"
              aria-label="Pinterest"
            >
              <svg width="18" height="18" viewBox="0 0 24 24" fill="currentColor" xmlns="http://www.w3.org/2000/svg"><path d="M12 0C5.373 0 0 5.372 0 12c0 5.084 3.163 9.426 7.627 11.174-.105-.949-.2-2.404.042-3.44.218-.937 1.407-5.965 1.407-5.965s-.359-.719-.359-1.782c0-1.668.967-2.914 2.171-2.914 1.023 0 1.518.769 1.518 1.69 0 1.029-.655 2.568-.993 3.995-.283 1.194.599 2.169 1.777 2.169 2.133 0 3.772-2.249 3.772-5.495 0-2.873-2.064-4.882-5.012-4.882-3.414 0-5.418 2.561-5.418 5.207 0 1.031.397 2.138.893 2.738.098.119.112.224.083.345l-.333 1.36c-.053.22-.174.267-.402.161-1.499-.698-2.436-2.888-2.436-4.649 0-3.785 2.75-7.262 7.929-7.262 4.163 0 7.398 2.967 7.398 6.931 0 4.136-2.607 7.464-6.227 7.464-1.216 0-2.36-.632-2.75-1.378 0 0-.602 2.294-.748 2.856-.271 1.043-1.002 2.35-1.492 3.146C9.57 23.812 10.763 24 12 24c6.627 0 12-5.373 12-12S18.627 0 12 0z"/></svg>
            </a>
          </div>
        </div>
      </div>

      <div className="container-x mt-16 pt-8 border-t border-white/10 flex flex-col md:flex-row items-center justify-between gap-4">
        <p className="text-xs text-white/40">
          © {new Date().getFullYear()} {BRAND.fullName}. Alle rechten voorbehouden.
        </p>
        <div className="flex items-center gap-5">
          <Link
            to="/voorwaarden"
            data-testid="footer-voorwaarden-link"
            className="text-xs text-white/50 hover:text-gold transition-colors uppercase tracking-[0.2em]"
          >
            Voorwaarden &amp; Privacy
          </Link>
          <Link
            to="/verhuurvoorwaarden-magic-mirror"
            data-testid="footer-verhuurvoorwaarden-link"
            className="text-xs text-white/50 hover:text-gold transition-colors uppercase tracking-[0.2em]"
          >
            Verhuurvoorwaarden
          </Link>
          <p className="text-xs text-white/40 font-script text-lg text-gold">
            Herinneringen die je voor altijd koestert
          </p>
        </div>
      </div>
    </footer>
  );
};

export default Footer;
